<script setup lang="ts">
import '@/assets/scss/form.scss'
import CSInputText from '@/onenetwork/components/form/CSInputText.vue'
import ImagePicker from '@/onenetwork/components/image_picker/ImagePicker.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import { useConfirm } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import AccountAPI from '../constants/account.api.uri.const.ts'
import { schemaUpdateBOAccount } from '../helper/validationAccountSchema.ts'
import { useAccountBODetailStore } from '../stores/account.bo.detail.store.ts'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'
import { Icon } from '@iconify/vue/dist/iconify.js'
import { initialValuesAccountBO } from '../helper/form.ts'
import CsButton from '@/components/ui/button/CsButton.vue'

const accountBODetailStore = useAccountBODetailStore()

const confirm = useConfirm()
const emit = defineEmits(['update:editable'])
const statusOptions = [
  { name: 'Hoạt động', code: true },
  { name: 'Không Hoạt động', code: false },
]
interface Props {
  id: number | null
  action: 'create' | 'update'
}

const props = defineProps<Props>()
const formRef = ref()
const loading = ref(false)
const account = computed(() => {
  const acc: any = accountBODetailStore.accounts[props.id]?.account || {}
  return acc;
})

const initialValues = ref(initialValuesAccountBO(account.value))

const toast = useToast()

const resolver = yupResolver(schemaUpdateBOAccount)

watch(() => props.id, () => {
  initialValues.value = initialValuesAccountBO(account.value)
}, {immediate: true, deep: true})

const updateBOAccount = async (data: any) => {
  loading.value = true
  try {
    const res = await APIClient.put(AccountAPI.bo.update.replace(':id', data.id.toString()), data)
    if (res.data.code === 200) {
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Cập nhật thông tin thành công',
        life: 3000,
      })
      initialValues.value = initialValuesAccountBO(res.data.data);
      editable.value = false
      loading.value = false
      nextTick(() => {
        formRef.value?.reset();
      })
    }
  } catch (e: any) {
    loading.value = false
    const errorMessage = e.response?.data?.errors[0]?.error || 'Đã có lỗi xảy ra'
    toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 })
  }
}

const onFormSubmit = ({ values, valid }: { values: Record<string, any>; valid: boolean }) => {
  if (!valid) return

  confirm.require({
    message: 'Bạn có muốn lưu các chỉnh sửa?',
    header: 'Xác nhận',
    // icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      updateBOAccount(values)
    },
    reject: () => {
      // toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
    },
  })
}

const editable = ref(false);

const handleChangeAvatar = (data: Object) => {
  if (formRef.value && data?.uuid) {
    formRef.value.setFieldValue('profile_image', data.uuid)
  }
}

const handleRemoveAvatar = (data: Object) => {
  console.log(data, 'data');
  formRef.value.setFieldValue('profile_image', '')
}

const selectStatus = ref({
  activation_status: account.value.activation_status
    ? statusOptions[0].code
    : statusOptions[1].code,
})

watch(
  () => account.value.activation_status,
  (newVal) => {
    selectStatus.value.activation_status = newVal
  },
)

onMounted(() => {
  //TODO: Mock Data
  // accountBODetailStore.fetchAccountBO(props.id)
})

const cancelEdit = () => {
  editable.value = false;
  formRef.value?.reset()
}

</script>

<template>
  <div class="flex flex-3 flex-col">
    <Card>
      <template #title>
        <div class="flex justify-between">
          <div v-if="account">
            Thông tin tài khoản: [#{{ account?.code }}] {{ account?.full_name }}
          </div>
          <div class="flex gap-2" v-if="account?.deleted_at === null">
            <AllowPerm perm="account.edit_bo_detail" action="hide">
              <CsButton
                type="button"
                icon="ri:edit-2-fill"
                text="Chỉnh sửa"
                :visible="!editable"
                @click="editable = true"
              />
              <CsButton
                type="button"
                icon="ri:close-circle-fill"
                text="Huỷ chỉnh sửa"
                variant="danger"
                :visible="editable"
                @click="cancelEdit"
              />
            </AllowPerm>
          </div>
        </div>
      </template>
      <template #content>
        <div class="pt-3">
          <Form
            v-slot="$form"
            ref="formRef"
            :initial-values="initialValues"
            :resolver="resolver"
            @submit="onFormSubmit"
            class="flex w-full"
          >
            <InputText name="profile_image" type="text" hidden class="form-control" />
            <InputText name="permissions" type="text" hidden class="form-control" />
            <InputText name="id" type="text" hidden class="form-control" />
            <div class="flex flex-1" v-if="editable">
              <image-picker
                type="photo"
                :uuid="uuid.v4().toString()"
                folder="user"
                height="150px"
                :src="initialValues.avatar"
                :upload="true"
                @action:upload-success="handleChangeAvatar"
                @action:remove-image="handleRemoveAvatar"
              />
            </div>
            <div class="flex flex-1" v-else>
              <image-picker readonly height="150px" type="photo" :src="initialValues.avatar" />
            </div>
            <div class="flex flex-col flex-3">
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Mã tài khoản:</div>
                <div class="flex-2">
                  <InputText
                    name="code"
                    type="text"
                    class="form-control"
                    disabled
                  />
                </div>
              </div>
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Họ và tên:</div>
                <div class="flex-2">
                  <CSInputText
                    :model-value="initialValues?.full_name"
                    name="full_name"
                    type="text"
                    class="form-control"
                    :editable="!editable"
                  />
                  <span class="italic text-[12px] text-red-500" v-if="$form.full_name?.invalid">
                    {{ $form.full_name.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Số điện thoại:</div>
                <div class="flex-2">
                  <CSInputText
                    :model-value="initialValues?.phone_number"
                    name="phone_number"
                    type="text"
                    class="form-control"
                    :editable="!editable"
                    maxlength="10"
                    @keypress="
                      (e) => {
                        const charCode = e.which ? e.which : e.keyCode
                        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                          e.preventDefault()
                        }
                      }
                    "
                    @paste="
                      (e: ClipboardEvent) => {
                        e.preventDefault()
                        const text = e.clipboardData?.getData('text') || ''
                        const numbers = text.replace(/\D/g, '').slice(0, 10)
                        formRef.value?.setFieldValue('phone_number', numbers)
                      }
                    "
                  />
                  <span class="italic text-[12px] text-red-500" v-if="$form.phone_number?.invalid">
                    {{ $form.phone_number.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Email:</div>
                <div class="flex-2">
                  <CSInputText
                    :model-value="initialValues?.email"
                    name="email"
                    type="text"
                    class="form-control"
                    :disabled="!editable"
                  />
                  <span class="italic text-[12px] text-red-500" v-if="$form.email?.invalid">
                    {{ $form.email.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Mật khẩu:</div>
                <div class="flex-2">
                  <CSInputText
                    :model-value="initialValues?.password"
                    name="password"
                    type="password"
                    class="form-control"
                    :disabled="!editable"
                  />
                  <span class="italic text-[12px] text-red-500" v-if="$form.password?.invalid">
                    {{ $form.password.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3">
                <div class="flex-1 text-right">Xác nhận mật khẩu:</div>
                <div class="flex-2">
                  <CSInputText
                    :model-value="initialValues?.password_confirmation"
                    name="password_confirmation"
                    type="password"
                    class="form-control"
                    :disabled="!editable"
                  />
                  <span
                    class="italic text-[12px] text-red-500"
                    v-if="$form.password_confirmation?.invalid"
                  >
                    {{ $form.password_confirmation.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3" v-if="account.deleted_at === null">
                <div class="flex-1 text-right">Trạng thái hoạt động:</div>
                <div class="flex-2">
                  <Select
                    name="activation_status"
                    v-model="selectStatus.activation_status"
                    :options="statusOptions"
                    optionLabel="name"
                    optionValue="code"
                    placeholder="Chọn trạng thái"
                    :disabled="!editable"
                    class="form-control"
                  />
                  <span
                    class="italic text-[12px] text-red-500"
                    v-if="$form.activation_status?.invalid"
                  >
                    {{ $form.activation_status.error.message }}
                  </span>
                </div>
              </div>
              <div class="flex mb-3 gap-3" v-if="account.deleted_at !== null">
                <div class="flex-1 text-right">Trạng thái:</div>
                <div class="flex-2">
                  <TagCell
                    :data="[{ type: 'danger', label: 'Đã khóa' }]"
                    :cell-config="{
                      class: 'w-full',
                    }"
                  />
                </div>
              </div>
              <div class="flex mb-3 gap-3" v-if="editable">
                <div class="flex-1 text-right"></div>
                <div class="flex-2">
                  <CsButton
                    type="submit"
                    :disabled="loading"
                    :loading="loading"
                    text="Lưu chỉnh sửa"
                  />
                </div>
              </div>
            </div>
          </Form>
        </div>
      </template>
    </Card>
  </div>
</template>
